import { useState, useEffect } from 'react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import ChatInterfaceFull from './components/ChatInterfaceFull'
import Login from './components/Login'
import { lightTheme, darkTheme } from './theme'
import './App.css'

// Read initial state synchronously to prevent flash
const getInitialState = () => {
  const token = localStorage.getItem('cme_token')
  const userData = localStorage.getItem('cme_user')
  let hasToken = false
  let isDark = false

  if (token && userData) {
    try {
      const user = JSON.parse(userData)
      isDark = user.theme === 'dark'

      // Redirect super_admin to their dashboard
      if (user.role === 'super_admin') {
        window.location.href = 'super-admin.html'
        return { hasToken: false, isDark: false }
      }

      hasToken = true
    } catch (e) {
      // ignore parse errors
    }
  }

  // Apply dark class immediately to prevent flash
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }

  return { hasToken, isDark }
}

const initialState = getInitialState()

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null) // null = checking, true/false = known
  const [isDarkMode, setIsDarkMode] = useState(initialState.isDark)

  // Verify token on mount and refresh user data
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('cme_token')
      if (!token) {
        setIsAuthenticated(false)
        return
      }

      try {
        // Use /users/me to both verify token AND get fresh user data
        const response = await fetch('/users/me', {
          headers: { Authorization: `Bearer ${token}` }
        })

        if (response.ok) {
          const userData = await response.json()
          localStorage.setItem('cme_user', JSON.stringify(userData))
          setIsAuthenticated(true)
        } else {
          // Token invalid - clear storage and show login
          localStorage.removeItem('cme_token')
          localStorage.removeItem('cme_user')
          setIsAuthenticated(false)
        }
      } catch (e) {
        // Network error - assume token is valid and let ChatInterfaceFull handle it
        setIsAuthenticated(initialState.hasToken)
      }
    }

    verifyToken()
  }, [])

  // Listen for theme changes from ChatInterfaceFull
  useEffect(() => {
    const handleThemeChange = () => {
      const userData = localStorage.getItem('cme_user')
      if (userData) {
        try {
          const user = JSON.parse(userData)
          setIsDarkMode(user.theme === 'dark')
        } catch (e) {
          // ignore parse errors
        }
      }
    }

    window.addEventListener('themeChanged', handleThemeChange)
    return () => window.removeEventListener('themeChanged', handleThemeChange)
  }, [])

  const handleLoginSuccess = () => {
    setIsAuthenticated(true)
  }

  const handleLogout = () => {
    localStorage.removeItem('cme_token')
    localStorage.removeItem('cme_user')
    localStorage.removeItem('cme_company')
    localStorage.removeItem('cme_original_token')
    localStorage.removeItem('cme_impersonating')
    setIsAuthenticated(false)
  }

  // Show nothing while checking auth
  if (isAuthenticated === null) {
    return null
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Login onLoginSuccess={handleLoginSuccess} />
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <ChatInterfaceFull onLogout={handleLogout} />
    </ThemeProvider>
  )
}

export default App
